import { Delete } from "@mui/icons-material";
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { IconButton, MenuItem, Select, Stack, TextField, Typography, Button, Box } from "@mui/material";
import React, { useContext, useEffect, useRef, useState } from "react";
import transactionServices from "../../../services/transactionServices";
import { useSnackbar } from "notistack";
import DataGridTable from "../../../components/DataGridTabel";
import { InvestCardComponent } from "./InvestPages/InvestCardComponent";

const BeneficiaryCard = ({beneficiaryInfo, setBeneficiaryInfo, transaction, selectedMode ,entityId ,entityType,listingId,user,showButton=false,rightToOwnerShip}) => {
    const ref = useRef()
    const { enqueueSnackbar } = useSnackbar();
    const [editMode, setEditMode] = useState(false);
    const [originalBeneficiaryInfo, setOriginalBeneficiaryInfo] = useState([]);

    const relationOptions = [
        {
            title: "Parent",
            value: "Parent",
        },
        {
            title: "Spouse",
            value: "Spouse",
        },
        {
            title: "In Law",
            value: "In Law",
        },
        {
            title: "Sibling",
            value: "Sibling",
        },
        {
            title: "Child",
            value: "Child",
        },
        {
            title: "Other Relative",
            value: "Other Relative",
        },
        {
            title: "Friend / Not Related",
            value: "Friend / Not Related",
        },
        {
            title: "Nominee / Attorney",
            value: "Nominee / Attorney",
        },
    ];
    
    const [ren, setRen] = useState(false);
    
    const columns = [
        { field: 'name', headerName: 'Full Legal Name', flex: 1 },
        { field: 'relation', headerName: 'Relationship', flex: 1 },
        { field: 'ownershipPercentage', headerName: 'Ownership %', flex: 1 },
    ];
    
    const getBeneficiaryInfo = async () => {    
        const data = {
            "listingId": listingId,
            "entityId": entityId ? entityId : user?._id,
           ...(rightToOwnerShip !== undefined && { "rightToOwnerShip": rightToOwnerShip })
        }
        const res = await transactionServices.getBeneficiaryInfo(data);
        
        if (res && res.success) {
            setBeneficiaryInfo(res.data.beneficiaries);
            setOriginalBeneficiaryInfo(JSON.parse(JSON.stringify(res.data.beneficiaries)));
        }
        else{
            setBeneficiaryInfo([{ name: "", relation: "", ownershipPercentage: "" }]);
            setOriginalBeneficiaryInfo([{ name: "", relation: "", ownershipPercentage: "" }]);
        }
    }

    useEffect(()=>{        
    getBeneficiaryInfo();
    },[entityId, entityType, listingId,rightToOwnerShip,user])

    useEffect(() => {
        if (ref.current && !ren) {
            setRen(true);
        }
    }, [ref, ren])

    useEffect(() => {
        if (Array.isArray(beneficiaryInfo) && beneficiaryInfo.length > 0) {
            const updatedBeneficiaries = [...beneficiaryInfo];
            if (!updatedBeneficiaries[0].ownershipPercentage) {
                updatedBeneficiaries[0] = { ...updatedBeneficiaries[0], ownershipPercentage: "100" };
                setBeneficiaryInfo(updatedBeneficiaries);
            }
        } else if (!Array.isArray(beneficiaryInfo)) {
            setBeneficiaryInfo([{ ownershipPercentage: "100" }]);
        }
    }, [beneficiaryInfo]); // Restored original dependency

    // Calculate sum of percentages excluding the last beneficiary
    const calculateSumExcludingLast = (beneficiaries) => {
        if (!Array.isArray(beneficiaries) || beneficiaries.length <= 1) return 0;
        return beneficiaries.slice(0, -1).reduce((sum, ben) => {
            return sum + (parseFloat(ben.ownershipPercentage) || 0);
        }, 0);
    };

    // Auto-adjust last beneficiary percentage
    const adjustLastBeneficiaryPercentage = (beneficiaries) => {
        if (!Array.isArray(beneficiaries) || beneficiaries.length <= 1) {
            return;
        }

        const sumExcludingLast = calculateSumExcludingLast(beneficiaries);
        const lastBeneficiaryPercentage = Math.max(0, (100 - sumExcludingLast)).toFixed(1);

        const updatedBeneficiaries = [...beneficiaries];
        updatedBeneficiaries[beneficiaries.length - 1] = {
            ...beneficiaries[beneficiaries.length - 1],
            ownershipPercentage: lastBeneficiaryPercentage
        };

        setBeneficiaryInfo(updatedBeneficiaries);
    };

    // Handle percentage change for non-last beneficiaries
    const handlePercentageChange = (index, value) => {
        if (!Array.isArray(beneficiaryInfo)) return;

        // Only allow editing if it's not the last beneficiary
        if (index === beneficiaryInfo.length - 1) return;

        if (/^\d*\.?\d{0,1}$/.test(value) && (value === "" || value === "." || parseFloat(value) <= 100)) {
            const updatedBeneficiaries = [...beneficiaryInfo];

            // Calculate the sum of other beneficiaries' percentages (excluding current and last)
            const sumOfOthers = updatedBeneficiaries.reduce((sum, ben, i) => {
                if (i !== index && i !== updatedBeneficiaries.length - 1) {
                    return sum + (parseFloat(ben.ownershipPercentage) || 0);
                }
                return sum;
            }, 0);

            // Calculate maximum allowed value for current beneficiary
            const maxAllowed = 100 - sumOfOthers;

            // If the new value would exceed 100%, cap it at the maximum allowed
            const newValue = value === "" || value === "." ? value :
                parseFloat(value) > maxAllowed ? maxAllowed.toString() : value;

            // Format the value to have one decimal place if it's a complete number
            const formattedValue = newValue.includes(".") ? newValue :
                newValue === "" ? newValue :
                    parseFloat(newValue).toFixed(1);

            updatedBeneficiaries[index] = {
                ...updatedBeneficiaries[index],
                ownershipPercentage: formattedValue
            };

            setBeneficiaryInfo(updatedBeneficiaries);

            // Auto adjust last beneficiary's percentage
            adjustLastBeneficiaryPercentage(updatedBeneficiaries);
        }
    };

    const removeBeneficiary = (index) => {
        if (Array.isArray(beneficiaryInfo) && beneficiaryInfo.length > 1) {
            const updatedBeneficiaries = [...beneficiaryInfo];
            updatedBeneficiaries.splice(index, 1);

            // If only one beneficiary remains, set its percentage to 100.0
            if (updatedBeneficiaries.length === 1) {
                updatedBeneficiaries[0] = {
                    ...updatedBeneficiaries[0],
                    ownershipPercentage: "100.0"
                };
            }

            setBeneficiaryInfo(updatedBeneficiaries);
            // Adjust percentages after removal
            adjustLastBeneficiaryPercentage(updatedBeneficiaries);
        }
    };

    const handleSubmit = async () => {
        const data = {
            "listingId": listingId,
            "entityId": entityId ? entityId : user?._id,
            "beneficiaries": beneficiaryInfo,
            "rightToOwnerShip": rightToOwnerShip
        }
        const res = await transactionServices.updateBeneficiary(data);
        if(res && res.success){
            enqueueSnackbar(res.message, { variant: "success" });
            setEditMode(false);
            setOriginalBeneficiaryInfo(JSON.parse(JSON.stringify(beneficiaryInfo)));
        }
        else{
            enqueueSnackbar(res.message, { variant: "error" });
        }
    }

    const handleEditMode = () => {
        setEditMode(true);
        // Store a deep copy of the current data when entering edit mode
        setOriginalBeneficiaryInfo(JSON.parse(JSON.stringify(beneficiaryInfo)));
    }

    const handleCancel = () => {
        setEditMode(false);
        // Restore the original data when canceling
        setBeneficiaryInfo(JSON.parse(JSON.stringify(originalBeneficiaryInfo)));
    }

    const addNewBeneficiary = () => {
      const updatedBeneficiaries = [...beneficiaryInfo];
    
      if (updatedBeneficiaries.length > 0) {
        // Start from the last index and move backwards
        let indexToReduce = updatedBeneficiaries.length - 1;
    
        while (
          indexToReduce >= 0 &&
          parseFloat(updatedBeneficiaries[indexToReduce].ownershipPercentage) <= 1
        ) {
          indexToReduce--;
        }
    
        if (indexToReduce >= 0) {
          // Found a beneficiary with >1%, subtract 1 from them
          const currentPercentage = parseFloat(
            updatedBeneficiaries[indexToReduce].ownershipPercentage
          );
    
          updatedBeneficiaries[indexToReduce] = {
            ...updatedBeneficiaries[indexToReduce],
            ownershipPercentage: (currentPercentage - 1).toFixed(1),
          };
        } 
      }
    
      // Add new beneficiary with 1% ownership
      updatedBeneficiaries.push({name: "",relation: "",ownershipPercentage: "1.0"});
    
      setBeneficiaryInfo(updatedBeneficiaries);
    };
    

    return (
        <InvestCardComponent title={showButton ? "" : `${!transaction ? "Add" : ""} Beneficiary`}>
        {showButton && !editMode ? (
            <>
                <Box>
                    <DataGridTable initialColumns={columns} initialRows={beneficiaryInfo} />
                </Box>
                <Stack direction="row" justifyContent="flex-end" width="100%" sx={{ mt: 1 }}>
                    <Button 
                        variant="outlined" 
                        onClick={handleEditMode}
                    >
                        Edit
                    </Button>
                </Stack>
            </>
        ) : (
            <Stack direction="column" width="100%" maxWidth={'700px'} spacing={2}>
              {Array.isArray(beneficiaryInfo) && (
                beneficiaryInfo?.map((beneficiary, index) => {
                  return(
                  <Stack
                    key={index}
                    direction={{ md: "row", xs: "column" }}
                    gap={{ md: 1.5, xs: 1.5 }}
                    width={"100%"}
                    alignItems="center"
                  >
                    <Stack direction="column" width={{ md: "28%", xs: "100%" }}>
                      <Typography variant="caption">Enter Full Legal Name*</Typography>
                      <TextField
                        size="small"
                        sx={{ "& .MuiInputBase-root": { minWidth: "120px", height: "40px" } }}
                        disabled={transaction}
                        inputProps={{ readOnly: transaction }}
                        value={beneficiary.name}
                        onChange={(e) => {
                          const inputValue = e.target.value;
                          if (/^[a-zA-Z ]*$/.test(inputValue)) {
                            const updated = [...beneficiaryInfo];
                            updated[index] = { ...beneficiary, name: inputValue };
                            setBeneficiaryInfo(updated);
                          }
                        }}
                      />
                    </Stack>
          
                    <Stack direction="column" width={{ md: "28%", xs: "100%" }}>
                      <Typography variant="caption">Select Relationship*</Typography>
                      <Select
                        size="small"
                        sx={{
                          minWidth: "120px !important",
                          height: "40px !important",
                          "& .MuiSelect-select": {
                            padding: "8.5px 14px !important",
                            height: "23px !important"
                          }
                        }}
                        disabled={transaction}
                        ref={index === 0 ? ref : null}
                        inputProps={{ readOnly: transaction }}
                        MenuProps={{
                          PaperProps: {
                            style: {
                              zIndex: 2000,
                            },
                          },                         
                        }}
                        value={beneficiary?.relation}
                        onChange={(e) => {
                          const updated = [...beneficiaryInfo];
                          updated[index] = { ...beneficiary, relation: e.target.value };
                          setBeneficiaryInfo(updated);
                        }}
                        displayEmpty
                        IconComponent={() => (
                          <ExpandMoreIcon sx={{ width: 18, height: 18, color: "text.disabled" }} />
                        )}
                      >
                        {relationOptions.map((e, i) => (
                          <MenuItem key={i} value={e.value}>
                            <Typography variant="placeHolder">{e.title}</Typography>
                          </MenuItem>
                        ))}
                      </Select>
                    </Stack>
          
                    <Stack direction="column" width={{ md: "28%", xs: "100%" }}>
                      <Typography variant="caption">Enter Ownership %*</Typography>
                      <TextField
                        size="small"
                        sx={{ "& .MuiInputBase-root": { minWidth: "120px", height: "40px" } }}
                        disabled={transaction || (index === beneficiaryInfo.length - 1 && beneficiaryInfo.length > 1)}
                        inputProps={{
                          readOnly: transaction || (index === beneficiaryInfo.length - 1 && beneficiaryInfo.length > 1),
                          inputMode: "decimal"
                        }}
                        value={beneficiary.ownershipPercentage}
                        onChange={(e) => handlePercentageChange(index, e.target.value)}
                        onBlur={(e) => {
                          const value = e.target.value;
                          if (value === "" || value === "." || parseFloat(value) === 0) {
                            handlePercentageChange(index, "1.0");
                          } else {
                            handlePercentageChange(index, parseFloat(value).toFixed(1));
                          }
                        }}
                      />
                    </Stack>
          
                    {/* Buttons */}
                    <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent={{md:"flex-start", xs:"flex-end"}}
                      // width={{ md: "16%", xs: "100%" }}
                      spacing={1}
                    >
                      {!transaction && beneficiaryInfo.length > 1 && (
                        <IconButton
                          onClick={() => removeBeneficiary(index)}
                          sx={{ p: 0.5 }}
                        >
                          <Delete color="error" />
                        </IconButton>
                      )}
                      {!transaction &&
                        index === beneficiaryInfo.length - 1 &&
                        beneficiaryInfo.length < 10 &&
                        selectedMode === 0 &&
                        beneficiary.name &&
                        beneficiary.relation &&
                        parseFloat(beneficiary.ownershipPercentage) > 0 && (
                          <IconButton
                            onClick={addNewBeneficiary}
                            sx={{ p: 0.5 }}
                          >
                            <AddCircleOutlineIcon color="secondary" />
                          </IconButton>
                        )}
                    </Stack>
                  </Stack>
                )})
              )}
              {Array.isArray(beneficiaryInfo) && beneficiaryInfo.length > 1 && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                  Last beneficiary's ownership percentage is auto-calculated from remaining percentage
                </Typography>
              )}
            </Stack>
        )}
        {(showButton && editMode) && (
            <Stack direction="row" justifyContent="flex-end" width="100%">  
                <Button 
                    sx={{mt:1, mr:2, alignSelf:"flex-end"}} 
                    variant="outlined" 
                    onClick={handleCancel}
                >
                    Cancel
                </Button>
                <Button 
                    disabled={(Array.isArray(beneficiaryInfo)
                        ? !beneficiaryInfo.every(b => b.name && b.relation && b.ownershipPercentage)
                        : !(beneficiaryInfo.name && beneficiaryInfo.relation && (beneficiaryInfo.ownershipPercentage == 100)))} 
                    sx={{mt:1, alignSelf:"flex-end"}} 
                    variant="contained" 
                    color="primary" 
                    onClick={handleSubmit}
                >
                    Save Changes
                </Button>
            </Stack>
        )}
      </InvestCardComponent>
      
    )
}

export default BeneficiaryCard;